import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";

const Toast = ({
  id,
  variant = "info",
  title,
  message,
  duration = 5000,
  position = "top-right",
  onClose,
  icon,
  showCloseButton = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    setTimeout(() => setIsVisible(true), 10);

    // Auto dismiss after duration
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          onClose?.(id);
        }, 300);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, id, onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose?.(id);
    }, 300); // Match animation duration
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "success":
        return {
          bg: "bg-success",
          bgLight: "bg-success-blur-01",
          border: "border-success",
          text: "text-white",
          icon: icon || "mdi:check-circle",
          iconBg: "bg-white-blur-05",
        };
      case "error":
        return {
          bg: "bg-danger",
          bgLight: "bg-red-50",
          border: "border-danger",
          text: "text-white",
          icon: icon || "mdi:alert-circle",
          iconBg: "bg-white-blur-05",
        };
      case "warn":
        return {
          bg: "bg-yellow-500",
          bgLight: "bg-yellow-50",
          border: "border-yellow-500",
          text: "text-white",
          icon: icon || "mdi:alert",
          iconBg: "bg-white-blur-05",
        };
      case "info":
      default:
        return {
          bg: "bg-primary",
          bgLight: "bg-primary-blur-01",
          border: "border-primary",
          text: "text-white",
          icon: icon || "mdi:information",
          iconBg: "bg-white-blur-05",
        };
    }
  };

  const getAnimationStyle = () => {
    const isTop = position.startsWith("top");
    const isLeft = position.includes("left");
    const isRight = position.includes("right");
    const isCenter = position.includes("center");

    let translateX = 0;
    let translateY = 0;

    if (isExiting || !isVisible) {
      if (isTop) {
        if (isLeft) translateX = -100;
        else if (isRight) translateX = 100;
        else if (isCenter) translateY = -100;
        else translateX = 100;
      } else {
        if (isLeft) translateX = -100;
        else if (isRight) translateX = 100;
        else if (isCenter) translateY = 100;
        else translateX = 100;
      }
    }

    return {
      opacity: isExiting ? 0 : isVisible ? 1 : 0,
      transform: `translate(${translateX}%, ${translateY}%) scale(${
        isExiting || !isVisible ? 0.95 : 1
      })`,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    };
  };

  const variantStyles = getVariantStyles();

  return (
    <div style={getAnimationStyle()}>
      <div
        className={`${variantStyles.bg} ${
          variantStyles.text
        } rounded-lg shadow-lg pointer-events-auto min-w-[320px] max-w-[420px] border-l-4 ${
          variantStyles.border
        } flex ${title ? "items-start" : "items-center"} gap-3 p-4`}
      >
        {/* Icon */}
        <div
          className={`${variantStyles.iconBg} rounded-full p-1.5 flex-shrink-0 mt-0.5 flex items-center justify-center`}
        >
          <Icon icon={variantStyles.icon} className="w-5 h-5 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className="font-semibold text-sm mb-1 leading-tight">
              {title}
            </h4>
          )}
          {message && (
            <p className="text-sm leading-relaxed opacity-95">{message}</p>
          )}
        </div>

        {/* Close Button */}
        {showCloseButton && (
          <button
            onClick={handleClose}
            className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity ml-2"
            aria-label="Close"
          >
            <Icon icon="mdi:close" className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Toast;
